<script lang="ts" setup>
import type { PageProps } from '@autobid/strapi-integration/composable/usePage'
import type { StrapiBuilderPageData, StrapiPageError } from '@autobid/strapi-integration/typescript/strapi/Page'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { usePage } from '@autobid/strapi-integration/composable/usePage'
import { useStrapiLang } from '@autobid/strapi-integration/composable/useStrapiLang'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { hasUserAccessToThisPage } from '@autobid/strapi-integration/utils/helpers'
import { useMainDomainLink } from '@autobid/ui/composables/useMainDomainLink'
import { useQuery } from '@tanstack/vue-query'
import { computed, onMounted, onServerPrefetch, watch } from 'vue'

const props = defineProps<PageProps>()
const emit = defineEmits(['error'])
const { strapiLang: locale } = useStrapiLang()
const { isAuthed, setPageRedirectData } = useAutobidAuth()
const { t } = useI18n()
const nuxtApp = useNuxtApp()
const { validate } = useMainDomainLink()

const { getPageQueryKey, fetchPage } = usePage()
const { data, suspense, isLoading } = useQuery(
  getPageQueryKey({ ...props, locale: locale.value }),
  async () => {
    try {
      return await fetchPage(props)
    }
    catch (error) {
      const { message, code, handled } = getError(error)

      if (!handled) {
        emit('error', {
          code,
          message,
        } as StrapiPageError)
      }

      return null
    }
  },
  {
    refetchOnWindowFocus: false,
  },
)
onServerPrefetch(suspense)

const pageData = computed<StrapiBuilderPageData | null>(() => {
  if (!data.value) {
    return null
  }

  if ('redirection' in data.value) {
    return null
  }

  return data.value as StrapiBuilderPageData
})
function pageMiddleware() {
  if (!data.value && !isLoading.value) {
    emit('error', {
      code: 404,
      message: t('error-page.404.description'),
    } as StrapiPageError)
    return
  }

  if (!data.value) {
    return
  }

  const redirection = 'redirection' in data.value ? data.value.redirection : null
  if (redirection) {
    return nuxtApp.runWithContext(() =>
      navigateTo(redirection.to, {
        redirectCode: redirection.code,
        replace: true,
        external: redirection.to.startsWith('http') && validate(redirection.to),
      }),
    )
  }

  const page = 'page' in data.value ? data.value.page : null
  if (!page) {
    return
  }

  const hasAccess = hasUserAccessToThisPage(
    page.access?.name || 'public',
    isAuthed.value,
  )
  if (!hasAccess) {
    if (isAuthed.value) {
      return navigateTo(`/${locale.value}`)
    }
    else {
      emit('error', {
        code: 401,
        message: t('error-page.401.description'),
      })
    }
  }
}

function updatePageRedirectData() {
  if (!pageData.value) {
    return
  }

  const willBeAccessAfterAuthChange = hasUserAccessToThisPage(
    pageData.value.page.access?.name || 'public',
    !isAuthed.value,
  )

  setPageRedirectData({ redirectable: willBeAccessAfterAuthChange })
}
onMounted(() => {
  updatePageRedirectData()
})
const route = useRoute()
watch(route, () => {
  updatePageRedirectData()
})

async function fetchOnClient() {
  // function to await in a top level component to avoid content shifts
  if (data.value) {
    return
  }

  await suspense()
}

await fetchOnClient()

pageMiddleware()

watch(data, () => {
  pageMiddleware()
})
</script>

<template>
  <div>
    <div v-if="pageData || allowEmpty" class="page">
      <slot :page-data="pageData" />
    </div>

    <client-only>
      <LazyCommonDialogTerms
        v-if="isAuthed && pageData?.page?.termsRequired"
      />
    </client-only>
  </div>
</template>
